import '@heytap/cloud-observation-rum-polyfill';
import * as api from '@opentelemetry/api';
import { SpanKind, ROOT_CONTEXT, context, trace } from '@opentelemetry/api';
import { GlobalAttributesSpanProcessor, GlobalAttributesLogRecordProcessor, setGlobalAttribute } from '@heytap/cloud-observation-shared-attributes';
import { Resource } from '@opentelemetry/resources';
import { SemanticResourceAttributes, SemanticAttributes } from '@opentelemetry/semantic-conventions';
import { RandomIdGenerator, SamplingDecision, BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import mitt from 'mitt';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { DocumentLoadInstrumentation, AttributeNames } from '@opentelemetry/instrumentation-document-load';
import { NavigationInstrumentation } from '@heytap/cloud-observation-instrumentation-navigation';
import { CloudObservationExceptionInstrumentation } from '@heytap/cloud-observation-instrumentation-exception';
import { UserInteractionInstrumentation } from '@heytap/cloud-observation-instrumentation-user-interaction';
import { PerformanceInstrumentation } from '@heytap/cloud-observation-instrumentation-performance';
import { CloudObservationResourceInstrumentation } from '@heytap/cloud-observation-instrumentation-resource';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { LongTaskInstrumentation } from '@opentelemetry/instrumentation-long-task';
import { RandomIdGenerator as RandomIdGenerator$1, hrTimeToNanoseconds, otperformance, isUrlIgnored, W3CTraceContextPropagator } from '@opentelemetry/core';
import { PerformanceTimingNames, hasKey, WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { onFCP, onTTFB } from 'web-vitals';
import { BatchLogRecordProcessor, LoggerProvider } from '@opentelemetry/sdk-logs';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import { SeverityNumber } from '@opentelemetry/api-logs';

// action
const ACTION_ID = 'ums.rum.action.id';
const ACTION_NAME = 'ums.rum.action.name';
const ACTION_DOM_MUTATION = 'ums.rum.action.dom.mution';

const EVENT_NAVIGATION_NAME = 'Navigation';
const INSTRUMENTATION = {
    NAVIGATION: '@heytap/cloud-observation-instrumentation-navigation',
    USER_INTERACTION: '@heytap/cloud-observation-instrumentation-user-interaction',
    DOCUMENT_LOAD: '@opentelemetry/instrumentation-document-load',
    RESOURCE: '@heytap/cloud-observation-instrumentation-resource',
    LONG_TASK: '@opentelemetry/instrumentation-long-task',
    XHR_TASK: '@opentelemetry/instrumentation-xml-http-request',
    FETCH_TASK: '@opentelemetry/instrumentation-fetch',
    EXCEPTION: '@heytap/cloud-observation-instrumentation-exception',
    PERFORMANCE: '@heytap/cloud-observation-instrumentation-performance',
};
const RUM_TYPE = 'ums.rum.type';
var RumType;
(function (RumType) {
    RumType["VIEW"] = "view";
    RumType["PERFORMANCE"] = "performance";
    RumType["ACTION"] = "action";
    RumType["LONGTASK"] = "longtask";
    RumType["ERROE"] = "error";
    RumType["RESOURCE"] = "resource";
})(RumType || (RumType = {}));

const BUFFER_MAX_SPANS = 2048;
const MAX_EXPORT_BATCH_SIZE = 15;
const BUFFER_TIMEOUT = 2000;
const SAME_ERROR_REPEAT_COUNT = 3;
const INSTRUMENTED_EVENT_NAMES = [
    'click',
    'dblclick',
    'submit',
    'reset',
    'pause',
    'play',
    'dragstart',
    'dragend',
    'drop',
];
const DEFAULT_RESOURCE_SAMPLE_RATE = 1;
const DEFAULT_TRACE_SAMPLE_RATE = 0.005;
const SemanticResourceAttributesExtension = {
    /** 部署版本. */
    SCREEN_SIZE: 'ums.rum.screen.size',
    UUID: 'ums.rum.uuid',
};
const MODULE_NAME = 'ums.rum.module.name';
const DEFAULT_LOG_LEVEL = 'INFO';
const locationUUIDKey = SemanticResourceAttributesExtension.UUID;

const LONG_TASK_ID = 'ums.rum.long_task.id';

// resource
const RESOURCE_XHR_NAME = 'HTTP';
const RESOURCE_TYPE = 'ums.rum.resource.type';
const RESOURCE_RESPONSE_HEADERS = 'response.headers';
const RESOURCE_RESPONSE_TEXT = 'response.text';
const SALVAGE = 'salvage';
const RESOURCE_TRACE_SAMPLED = 'ums.rum.trace.sampled';
const CORSP_REFLIGHT = 'CORS Preflight';
const ResourceType = {
    DOCUMENT: 'document',
    XHR: 'xhr',
    BEACON: 'beacon',
    FETCH: 'fetch',
    CSS: 'css',
    JS: 'js',
    IMAGE: 'image',
    FONT: 'font',
    MEDIA: 'media',
    OTHER: 'other',
};
const FAKE_INITIAL_DOCUMENT = 'initial_document';
const RESOURCE_TYPES_MAP = [
    [
        ResourceType.DOCUMENT,
        function (initiatorType) {
            return FAKE_INITIAL_DOCUMENT === initiatorType;
        },
    ],
    [
        ResourceType.XHR,
        function (initiatorType) {
            return initiatorType === 'xmlhttprequest';
        },
    ],
    [
        ResourceType.FETCH,
        function (initiatorType) {
            return initiatorType === 'fetch';
        },
    ],
    [
        ResourceType.BEACON,
        function (initiatorType) {
            return initiatorType === 'beacon';
        },
    ],
    [
        ResourceType.CSS,
        function (_, path) {
            return path.match(/\.css$/i) !== null;
        },
    ],
    [
        ResourceType.JS,
        function (_, path) {
            return path.match(/\.js$/i) !== null;
        },
    ],
    [
        ResourceType.IMAGE,
        function (initiatorType, path) {
            return (['image', 'img', 'icon'].includes(initiatorType) ||
                path.match(/\.(gif|jpg|jpeg|tiff|png|svg|ico)$/i) !== null);
        },
    ],
    [
        ResourceType.FONT,
        function (_, path) {
            return path.match(/\.(woff|eot|woff2|ttf)$/i) !== null;
        },
    ],
    [
        ResourceType.MEDIA,
        function (initiatorType, path) {
            return (['audio', 'video'].includes(initiatorType) ||
                path.match(/\.(mp3|mp4)$/i) !== null);
        },
    ],
];

const SESSION_ID_ATTRIBUTE = 'ums.rum.session.id';
const COOKIE_NAME = 'cloudObservationRumSessionId';
const RESOURCE_IS_SEND = 'resouce.is.send';

// view
const VIEW_NAME_ATTRIBUTE = 'ums.rum.view.name';
const VIEW_URL_ATTRIBUTE = 'ums.rum.view.url';
const VIEW_PATH_GROUP_ATTRIBUTE = 'ums.rum.view.path_group';
const VIEW_ID_ATTRIBUTE = 'ums.rum.view.id';
const PRE_VIEW_ID_ATTRIBUTE = 'ums.rum.view.referrer.id';
const PRE_VIEW_URL_ATTRIBUTE = 'ums.rum.view.referrer.url';
const EACH_PERFORMANCE = {
    // 每个view都可能有，这次view 携带
    VIEW_LOAD_TIME: 'ums.rum.view.loading_time',
};
const INIT_PERFORMANCE = {
    // 只有init_load有
    INIT_LOAD_FCP: 'first_contentful_paint',
    TIME_TO_FIRST_BYTE: 'time_to_first_byte',
    INIT_LOAD_LCP: 'largest_contentful_paint.time',
    FIRST_TIME: 'first_paint.time',
    DOM_INTERACTIVE: 'dom_interactive.time',
    DOM_CONTENT_LOADED: 'dom_content_loaded.time',
    DOM_COMPLETE: 'dom_complete.time',
    LOAD_EVENT: 'load_event.time',
    RESOURCE_LOAD_TIME: 'resource_load.time',
    DOM_PARSING_TIME: 'dom_parsing.time',
    DOM_READY_TIME: 'dom_ready.time', // DOM解析耗时
};

// console.debug()、console.info()、console.warn() 和 console.error()也是非常有用，常用于定义不同级别的日志。
// console.trace 追踪函数的执行栈。当你想知道一个函数具体是怎样被调用的时，可以使用console.trace这个函数去追踪它的执行栈。
const trackConsoleList = [
    {
        logLevel: 'ERROR',
        funField: 'error',
        originFun: console.error,
    },
    {
        logLevel: 'DEBUG',
        funField: 'log',
        // eslint-disable-next-line no-console
        originFun: console.log,
    },
    {
        logLevel: 'DEBUG',
        funField: 'debug',
        // eslint-disable-next-line no-console
        originFun: console.debug,
    },
    {
        logLevel: 'INFO',
        funField: 'info',
        // eslint-disable-next-line no-console
        originFun: console.info,
    },
    {
        logLevel: 'WARN',
        funField: 'warn',
        originFun: console.warn,
    },
    {
        logLevel: 'TRACE',
        funField: 'trace',
        // eslint-disable-next-line no-console
        originFun: console.trace,
    },
];

const is = (val, type) => {
    return toString.call(val) === `[object ${type}]`;
};
const isNumber = (val) => {
    return is(val, 'Number');
};
const tryNumber = (input) => {
    if (typeof input === 'number')
        return input;
    return input != null && Number.isFinite(+input) ? +input : undefined;
};
const idGenerator$1 = new RandomIdGenerator();
function uuid() {
    return idGenerator$1.generateTraceId();
}

// 截取掉字符串的循环调用，增加deep深度处理
const consoleValueToArgument = (value, usedReferences = new Set(), deep = 0) => {
    if (deep > 10)
        return;
    try {
        const addedToUsedReferences = !usedReferences.has(value);
        usedReferences.add(value);
        if (Array.isArray(value)) {
            return value.map(elem => consoleValueToArgument(elem, usedReferences, deep + 1));
        }
        else if (value instanceof Error) {
            // 对于错误单独处理，上报错误的stack
            return consoleValueToArgument(value.stack);
        }
        else if (value && typeof value === 'object') {
            const result = {};
            Object.keys(value).forEach((key) => {
                // 不使用getOwnPropertyNames，不可枚举的数据就不采集了,
                if (!key.includes('__zone_symbol_')) {
                    //  __zone_symbol_是zone包的一层过滤掉.
                    const keyValue = value[key];
                    const isCircular = usedReferences.has(keyValue) &&
                        (keyValue !== value || addedToUsedReferences);
                    result[key] = isCircular
                        ? '[Circular]'
                        : consoleValueToArgument(keyValue, usedReferences, deep + 1);
                }
            });
            return result;
        }
        return value;
    }
    catch (_a) { }
};
const getCollectionSourceUrl = (sourceUrl) => {
    const url = new URL(sourceUrl);
    return url.origin;
};
function maybeStringTrim(value) {
    return typeof value === 'string' ? value.trim() : value;
}
function safeTruncate(candidate, length, suffix = '') {
    const lastChar = candidate.charCodeAt(length - 1);
    const isLastCharSurrogatePair = lastChar >= 0xD800 && lastChar <= 0xDBFF;
    const correctedLength = isLastCharSurrogatePair ? length + 1 : length;
    if (candidate.length <= correctedLength)
        return candidate;
    return `${candidate.slice(0, correctedLength)}${suffix}`;
}
function isIE() {
    return Boolean(document.documentMode);
}
function find(array, predicate) {
    for (let i = 0; i < array.length; i += 1) {
        const item = array[i];
        if (predicate(item, i))
            return item;
    }
    return undefined;
}

const applyCustomAttributesOnXMLSpan = (span, xhr) => {
    span.setAttribute(RESOURCE_RESPONSE_TEXT, xhr.responseText);
    span.setAttribute(RESOURCE_RESPONSE_HEADERS, xhr.getAllResponseHeaders());
};
const applyCustomAttributesOnFetchSpan = (span, request, result) => {
    // 将格式和xhr获取到的xhr.getAllResponseHeaders()保持一致，方便后续解析
    const res = Object.entries(result.headers || {})
        .map(ele => ele.join(': '))
        .join('\n');
    span.setAttribute(RESOURCE_RESPONSE_HEADERS, res);
};
function formatAttributes(attributes = {}) {
    const entries = Object.entries(attributes || {}).map(([key, val]) => {
        return [`ums.rum.custom.${key.trim()}`, maybeStringTrim(val)];
    });
    return Object.fromEntries(entries) || {};
}

const getUserInteractionSpanNameId = (name) => {
    const nameIdArr = name.match(/'(.+)'/);
    return nameIdArr ? nameIdArr[1] : '';
};
const priorityStrategies = [
    // associated LABEL text
    (element, userProgrammaticAttribute) => {
        // IE does not support element.labels, so we fallback to a CSS selector based on the element id
        // instead
        if (supportsLabelProperty()) {
            if ('labels' in element && element.labels && element.labels.length > 0)
                return getTextualContent(element.labels[0], userProgrammaticAttribute);
        }
        else if (element.id) {
            const label = element.ownerDocument &&
                find(element.ownerDocument.querySelectorAll('label'), label => label.htmlFor === element.id);
            return label && getTextualContent(label, userProgrammaticAttribute);
        }
    },
    // INPUT button (and associated) value
    (element) => {
        if (element.nodeName === 'INPUT') {
            const input = element;
            const type = input.getAttribute('type');
            if (type === 'button' || type === 'submit' || type === 'reset')
                return input.value;
        }
    },
    // BUTTON, LABEL or button-like element text
    (element, userProgrammaticAttribute) => {
        if (element.nodeName === 'BUTTON' || element.nodeName === 'LABEL' || element.getAttribute('role') === 'button')
            return getTextualContent(element, userProgrammaticAttribute);
    },
    element => element.getAttribute('aria-label'),
    // associated element text designated by the aria-labelledby attribute
    (element, userProgrammaticAttribute) => {
        const labelledByAttribute = element.getAttribute('aria-labelledby');
        if (labelledByAttribute) {
            return labelledByAttribute
                .split(/\s+/)
                .map(id => getElementById(element, id))
                .filter((label) => Boolean(label))
                .map(element => getTextualContent(element, userProgrammaticAttribute))
                .join(' ');
        }
    },
    element => element.getAttribute('alt'),
    element => element.getAttribute('name'),
    element => element.getAttribute('title'),
    element => element.getAttribute('placeholder'),
    // SELECT first OPTION text
    (element, userProgrammaticAttribute) => {
        if ('options' in element && element.options.length > 0)
            return getTextualContent(element.options[0], userProgrammaticAttribute);
    },
];
const fallbackStrategies = [
    (element, userProgrammaticAttribute) => getTextualContent(element, userProgrammaticAttribute),
];
/**
 * Get the action name from the attribute 'ums-rum-action-name' on the element or any of its parent.
 * It can also be retrieved from a user defined attribute.
 */
const DEFAULT_PROGRAMMATIC_ACTION_NAME_ATTRIBUTE = 'ums-rum-action-name';
function getActionNameFromElement(element, userProgrammaticAttribute) {
    // Proceed to get the action name in two steps:
    // * first, get the name programmatically, explicitly defined by the user.
    // * then, use strategies that are known to return good results. Those strategies will be used on
    //   the element and a few parents, but it's likely that they won't succeed at all.
    // * if no name is found this way, use strategies returning less accurate names as a fallback.
    //   Those are much likely to succeed.
    return (getActionNameFromElementProgrammatically(element, DEFAULT_PROGRAMMATIC_ACTION_NAME_ATTRIBUTE) ||
        (userProgrammaticAttribute && getActionNameFromElementProgrammatically(element, userProgrammaticAttribute)) ||
        getActionNameFromElementForStrategies(element, userProgrammaticAttribute, priorityStrategies) ||
        getActionNameFromElementForStrategies(element, userProgrammaticAttribute, fallbackStrategies) ||
        '');
}
function getActionNameFromElementProgrammatically(targetElement, programmaticAttribute) {
    let elementWithAttribute;
    // We don't use getActionNameFromElementForStrategies here, because we want to consider all parents,
    // without limit. It is up to the user to declare a relevant naming strategy.
    // If available, use element.closest() to match get the attribute from the element or any of its
    // parent.  Else fallback to a more traditional implementation.
    if (supportsElementClosest()) {
        elementWithAttribute = targetElement.closest(`[${programmaticAttribute}]`);
    }
    else {
        let element = targetElement;
        while (element) {
            if (element.hasAttribute(programmaticAttribute)) {
                elementWithAttribute = element;
                break;
            }
            element = element.parentElement;
        }
    }
    if (!elementWithAttribute)
        return;
    const name = elementWithAttribute.getAttribute(programmaticAttribute);
    return truncate(normalizeWhitespace(name.trim()));
}
/**
 * Iterates over the target element and its parent, using the strategies list to get an action name.
 * Each strategies are applied on each element, stopping as soon as a non-empty value is returned.
 */
const MAX_PARENTS_TO_CONSIDER = 10;
function getActionNameFromElementForStrategies(targetElement, userProgrammaticAttribute, strategies) {
    let element = targetElement;
    let recursionCounter = 0;
    while (recursionCounter <= MAX_PARENTS_TO_CONSIDER &&
        element &&
        element.nodeName !== 'BODY' &&
        element.nodeName !== 'HTML' &&
        element.nodeName !== 'HEAD') {
        for (const strategy of strategies) {
            const name = strategy(element, userProgrammaticAttribute);
            if (typeof name === 'string') {
                const trimmedName = name.trim();
                if (trimmedName)
                    return truncate(normalizeWhitespace(trimmedName));
            }
        }
        // Consider a FORM as a contextual limit to get the action name.  This is experimental and may
        // be reconsidered in the future.
        if (element.nodeName === 'FORM')
            break;
        element = element.parentElement;
        recursionCounter += 1;
    }
}
function normalizeWhitespace(s) {
    return s.replace(/\s+/g, ' ');
}
function truncate(s) {
    return s.length > 100 ? `${safeTruncate(s, 100)} [...]` : s;
}
function getElementById(refElement, id) {
    // Use the element ownerDocument here, because tests are executed in an iframe, so
    // document.getElementById won't work.
    return refElement.ownerDocument ? refElement.ownerDocument.getElementById(id) : null;
}
function getTextualContent(element, userProgrammaticAttribute) {
    if (element.isContentEditable)
        return;
    if ('innerText' in element) {
        let text = element.innerText;
        const removeTextFromElements = (query) => {
            const list = element.querySelectorAll(query);
            for (let index = 0; index < list.length; index += 1) {
                const element = list[index];
                if ('innerText' in element) {
                    const textToReplace = element.innerText;
                    if (textToReplace && textToReplace.trim().length > 0)
                        text = text.replace(textToReplace, '');
                }
            }
        };
        if (!supportsInnerTextScriptAndStyleRemoval()) {
            // remove the inner text of SCRIPT and STYLES from the result. This is a bit dirty, but should
            // be relatively fast and work in most cases.
            removeTextFromElements('script, style');
        }
        // remove the text of elements with programmatic attribute value
        removeTextFromElements(`[${DEFAULT_PROGRAMMATIC_ACTION_NAME_ATTRIBUTE}]`);
        if (userProgrammaticAttribute)
            removeTextFromElements(`[${userProgrammaticAttribute}]`);
        return text;
    }
    return element.textContent;
}
/**
 * Returns true if element.innerText excludes the text from inline SCRIPT and STYLE element. This
 * should be the case everywhere except on Internet Explorer 10 and 11 (see [1])
 *
 * The innerText property relies on what is actually rendered to compute its output, so to check if
 * it actually excludes SCRIPT and STYLE content, a solution would be to create a style element, set
 * its content to '*', inject it in the document body, and check if the style element innerText
 * property returns '*'. Using a new `document` instance won't work as it is not rendered.
 *
 * This solution requires specific CSP rules (see [2]) to be set by the customer. We want to avoid
 * this, so instead we rely on browser detection. In case of false negative, the impact should be
 * low, since we rely on this result to remove the SCRIPT and STYLE innerText (which will be empty)
 * from a parent element innerText.
 *
 * [1]: https://web.archive.org/web/20210602165716/http://perfectionkills.com/the-poor-misunderstood-innerText/#diff-with-textContent
 * [2]: https://github.com/DataDog/browser-sdk/issues/1084
 */
function supportsInnerTextScriptAndStyleRemoval() {
    return !isIE();
}
/**
 * Returns true if the browser supports the element.labels property.  This should be the case
 * everywhere except on Internet Explorer.
 * Note: The result is computed lazily, because we don't want any DOM access when the SDK is
 * evaluated.
 */
let supportsLabelPropertyResult;
function supportsLabelProperty() {
    if (supportsLabelPropertyResult === undefined)
        supportsLabelPropertyResult = 'labels' in HTMLInputElement.prototype;
    return supportsLabelPropertyResult;
}
/**
 * Returns true if the browser supports the element.closest method.  This should be the case
 * everywhere except on Internet Explorer.
 * Note: The result is computed lazily, because we don't want any DOM access when the SDK is
 * evaluated.
 */
let supportsElementClosestResult;
function supportsElementClosest() {
    if (supportsElementClosestResult === undefined)
        supportsElementClosestResult = 'closest' in HTMLElement.prototype;
    return supportsElementClosestResult;
}

const emitter = mitt();

let rumUuid;
const setUuid = () => {
    rumUuid = uuid();
    localStorage.setItem(locationUUIDKey, rumUuid);
};
function getUuid() {
    const useLocalStorage = !!window.localStorage && typeof localStorage === 'object';
    if (useLocalStorage) {
        rumUuid = localStorage.getItem(locationUUIDKey);
        if (!rumUuid)
            setUuid();
        return rumUuid;
    }
    return '';
}

const resourceInitialize = ({ serviceName, deploymentEnvironment, deploymentVersion, resouceAttributes, }) => {
    const baseResourceAttributes = formatBaseAttributes({
        serviceName,
        deploymentEnvironment,
        deploymentVersion,
    });
    if (resouceAttributes instanceof Promise) {
        return {
            allResource: new Resource(baseResourceAttributes, resouceAttributes),
        };
    }
    else {
        const userResouceAttributes = formatAttributes(resouceAttributes);
        const allResourceAttributes = Object.assign(Object.assign({}, userResouceAttributes), baseResourceAttributes);
        return {
            allResource: new Resource(allResourceAttributes),
        };
    }
};
function formatBaseAttributes({ serviceName, deploymentEnvironment, deploymentVersion, moduleName, }) {
    const resourceAttributes = {
        [SemanticResourceAttributes.SERVICE_NAME]: serviceName,
        'ums.rum.version': "2.1.1",
    };
    resourceAttributes[SemanticResourceAttributesExtension.SCREEN_SIZE] = `${window.screen.width}*${window.screen.height}`;
    if (deploymentEnvironment) {
        resourceAttributes[SemanticResourceAttributes.DEPLOYMENT_ENVIRONMENT] =
            deploymentEnvironment;
    }
    const uuid = getUuid();
    if (uuid)
        resourceAttributes[SemanticResourceAttributesExtension.UUID] = uuid;
    if (deploymentVersion) {
        resourceAttributes[SemanticResourceAttributes.SERVICE_VERSION] =
            deploymentVersion;
    }
    if (moduleName)
        resourceAttributes[MODULE_NAME] = moduleName;
    if (navigator === null || navigator === void 0 ? void 0 : navigator.userAgent)
        resourceAttributes[SemanticAttributes.HTTP_USER_AGENT] = navigator.userAgent;
    return resourceAttributes;
}

const instrumentationInitialize = ({ collectionSourceUrl, ignoreUrls = [], trackViewsManually = false, propagateTraceHeaderCorsUrls = [], actionNameAttribute = 'ums-rum-action-name', }, traceProvider) => {
    let disableOpenTelemetryInstrumentations;
    const disableInstrumentations = () => {
        if (disableOpenTelemetryInstrumentations) {
            disableOpenTelemetryInstrumentations();
            disableOpenTelemetryInstrumentations = undefined;
        }
    };
    const registerInstrumentations$1 = () => {
        disableInstrumentations();
        const cloudObservationNavigationInstrumentation = new NavigationInstrumentation({
            enabled: false,
            trackViewsManually,
        });
        const cloudObservationResourceInstrumentation = new CloudObservationResourceInstrumentation({
            ignoreUrls: [new RegExp(`^${collectionSourceUrl}/\S*`), ...ignoreUrls],
            enabled: false,
        });
        const longTaskInstrumentation = new LongTaskInstrumentation({
            enabled: false,
        });
        const cloudObservationExceptionInstrumentation = new CloudObservationExceptionInstrumentation({
            enabled: false,
        });
        const documentLoadInstrumentation = new DocumentLoadInstrumentation({
            enabled: false,
        });
        const userInteractionInstrumentation = new UserInteractionInstrumentation({
            enabled: false,
            eventNames: INSTRUMENTED_EVENT_NAMES,
            shouldPreventSpanCreation: (eventType, element, span) => {
                const newName = getActionNameFromElement(element, actionNameAttribute);
                if (newName) {
                    span.updateName(`${eventType} on '${newName}'`);
                    span.setAttribute(ACTION_NAME, newName);
                    return false;
                }
                else {
                    return true;
                }
            },
        });
        const performanceInstrumentation = new PerformanceInstrumentation({
            enabled: false,
        });
        const xhrHttpRequestInstrumentation = new XMLHttpRequestInstrumentation({
            enabled: false,
            propagateTraceHeaderCorsUrls,
            ignoreUrls,
            applyCustomAttributesOnSpan: applyCustomAttributesOnXMLSpan,
        });
        const fetchInstrumentation = new FetchInstrumentation({
            enabled: false,
            propagateTraceHeaderCorsUrls,
            ignoreUrls,
            applyCustomAttributesOnSpan: applyCustomAttributesOnFetchSpan,
        });
        disableOpenTelemetryInstrumentations =
            registerInstrumentations({
                tracerProvider: traceProvider,
                instrumentations: [
                    cloudObservationNavigationInstrumentation,
                    cloudObservationResourceInstrumentation,
                    longTaskInstrumentation,
                    cloudObservationExceptionInstrumentation,
                    documentLoadInstrumentation,
                    userInteractionInstrumentation,
                    performanceInstrumentation,
                    xhrHttpRequestInstrumentation,
                    fetchInstrumentation,
                ],
            });
        return {
            cloudObservationNavigationInstrumentation,
            cloudObservationResourceInstrumentation,
            longTaskInstrumentation,
            cloudObservationExceptionInstrumentation,
            documentLoadInstrumentation,
            userInteractionInstrumentation,
            performanceInstrumentation,
            xhrHttpRequestInstrumentation,
            fetchInstrumentation,
        };
    };
    return {
        registerInstrumentations: registerInstrumentations$1,
        disableInstrumentations,
    };
};

const COOKIE_VALUE_SEPARATOR = '-';
const MAX_INACTIVITY_MS = 1000 * 60 * 5; // 5 minutes
const REFRESH_ACTIVITY_TIME_AFTER_MS = 1000 * 30; // 30 seconds
const getCookieValue = () => {
    const cookie = document.cookie
        .split('; ')
        .find(item => item.startsWith(`${COOKIE_NAME}=`));
    if (!cookie)
        return;
    const [sessionId, lastActivityTimestamp, serviceName = ''] = cookie
        .split('=')[1]
        .split(COOKIE_VALUE_SEPARATOR);
    return {
        sessionId,
        lastActivityTimestamp: parseInt(lastActivityTimestamp, 10),
        serviceName,
    };
};
const setCookieValue = ({ sessionId, lastActivityTimestamp, serviceName, }) => {
    document.cookie = `${COOKIE_NAME}=${sessionId}${COOKIE_VALUE_SEPARATOR}${lastActivityTimestamp}${COOKIE_VALUE_SEPARATOR}${serviceName}; path=/`;
};
const idGenerator = new RandomIdGenerator$1();
let cookie = getCookieValue();
const getCurrentSessionId = (rawServiceName = '') => {
    const serviceName = rawServiceName.split(COOKIE_VALUE_SEPARATOR).join('');
    // 调用getCurrentSessionId没有传递serviceName时候，此时获取不到service值，因此这里记录之前的service值
    let oldServiceName = '';
    const now = Date.now();
    if (cookie) {
        oldServiceName = cookie.serviceName;
        const inactivityDuration = now - cookie.lastActivityTimestamp;
        if (inactivityDuration > MAX_INACTIVITY_MS) {
            // we're no longer interested with session that was inactive for long time
            cookie = undefined;
        }
        else if (serviceName && serviceName !== cookie.serviceName) {
            cookie = undefined;
        }
        else if (inactivityDuration > REFRESH_ACTIVITY_TIME_AFTER_MS) {
            cookie.lastActivityTimestamp = now;
            setCookieValue(cookie);
        }
    }
    if (!cookie) {
        cookie = {
            sessionId: idGenerator.generateTraceId(),
            lastActivityTimestamp: now,
            serviceName: serviceName || oldServiceName,
        };
        setCookieValue(cookie);
    }
    return cookie.sessionId;
};
const onStart$2 = (span, serviceName) => {
    span.setAttribute(SESSION_ID_ATTRIBUTE, getCurrentSessionId(serviceName));
};

class SessionIdRatioBasedSampler {
    constructor(_sessionRatio, _traceRatio, // 判断后续服务采样率（结合resourceSampleRate）
    _resourceSampleRate) {
        this._sessionRatio = _sessionRatio;
        this._traceRatio = _traceRatio;
        this._resourceSampleRate = _resourceSampleRate;
        this.sessionCanSend = false;
        this._sessionRatio = _sessionRatio || 1;
        this._traceRatio = _traceRatio || 1;
        this._sessionUpperBound = Math.floor(_sessionRatio * 0xFFFFFFFF);
        this._traceUpperBound = Math.floor(_traceRatio * 0xFFFFFFFF);
    }
    shouldSample(context, traceId, spanName) {
        this._isSessionCollect();
        const { decision, attributes } = this._getSamplingDecision(traceId, spanName);
        return {
            decision,
            attributes,
        };
    }
    _isSessionCollect() {
        const currentSessionId = getCurrentSessionId();
        if (this.preSessionId !== currentSessionId) {
            this.sessionCanSend =
                this._accumulate(currentSessionId) < this._sessionUpperBound;
            this.preSessionId = currentSessionId;
        }
    }
    _isAfterTraceCollect(traceId) {
        // 针对后续服务trace是否上报处理
        return this._accumulate(traceId) < this._traceUpperBound;
    }
    _getSamplingDecision(traceId, spanName) {
        let isSample = this.sessionCanSend;
        // 判断是不是资源资源类型需要再结合_traceRatio，传递到后端标识不采样
        if (spanName.indexOf('HTTP') === 0 ||
            ['PUT', 'POST', 'DELETE', 'PATCH', 'GET'].includes(spanName) ||
            spanName === AttributeNames.RESOURCE_FETCH) {
            let afterTraceIsSend = false; // 该参数是用于设置后端采样的，前端服务不参考
            let attributes;
            /*
              _afterTraceIsSend、attributes联合传递resource时候标识RECORD_AND_SAMPLED不是抛弃这条resource;
              而是为了表示是请求头traceparent 的baddage（传播者）设置为00，告诉后续服务不采样，
              trace采样不影响本服务，_resourceSampleRate为true，这条resource，本服务还是要的
            */
            if (isSample) {
                isSample = this._resourceSampleRate >= Math.random();
                afterTraceIsSend = this._isAfterTraceCollect(traceId);
                if (!afterTraceIsSend)
                    attributes = { [RESOURCE_IS_SEND]: isSample };
            }
            return isSample && afterTraceIsSend
                ? { decision: SamplingDecision.RECORD_AND_SAMPLED }
                : { decision: SamplingDecision.RECORD, attributes };
        }
        else {
            return isSample
                ? { decision: SamplingDecision.RECORD_AND_SAMPLED }
                : { decision: SamplingDecision.NOT_RECORD };
        }
    }
    toString() {
        return `SessionIdRatioBased${this._sessionRatio}, TraceIdRatioBased${this._traceRatio}`;
    }
    _accumulate(id) {
        let accumulation = 0;
        for (let i = 0; i < id.length / 8; i++) {
            const pos = i * 8;
            const part = parseInt(id.slice(pos, pos + 8), 16);
            accumulation = (accumulation ^ part) >>> 0;
        }
        return accumulation;
    }
}

var RUMEvent;
(function (RUMEvent) {
    RUMEvent["SPANPROCESSEND"] = "span-process-end";
})(RUMEvent || (RUMEvent = {}));

const LONGTASK_PRECISION_NS = 1e7; // 10 milliseconds
const MAX_SPANS_SIZE$1 = 100;
const MAX_LONG_TASKS_SIZE = 10;
let spans$1 = [];
let longTasks = [];
const isTimeRangeInSpan$1 = (longtaskStartTimeNs, spanStartTimeNs, spanEndTimeNs) => longtaskStartTimeNs + LONGTASK_PRECISION_NS >= spanStartTimeNs &&
    longtaskStartTimeNs - LONGTASK_PRECISION_NS <= spanEndTimeNs;
const findBestSpanInTime$1 = (longtaskStartTimeHrTime) => {
    const longtaskStartTime = hrTimeToNanoseconds(longtaskStartTimeHrTime);
    for (let i = spans$1.length - 1; i >= 0; i -= 1) {
        const span = spans$1[i];
        if (isTimeRangeInSpan$1(longtaskStartTime, hrTimeToNanoseconds(span.startTime), hrTimeToNanoseconds(span.endTime)))
            return span;
    }
};
const attachLongTaskToSpan = (longTask, parentSpan) => {
    longTask.parentSpanId = parentSpan.spanContext().spanId;
    longTask.spanContext().traceId = parentSpan.spanContext().traceId;
};
const attachBestLongTasks = (span, onLongTaskFound) => {
    const startTime = hrTimeToNanoseconds(span.startTime);
    const endTime = hrTimeToNanoseconds(span.endTime);
    for (let i = longTasks.length - 1; i >= 0; i -= 1) {
        const longTask = longTasks[i];
        if (isTimeRangeInSpan$1(hrTimeToNanoseconds(longTask.startTime), startTime, endTime)) {
            longTasks.splice(i, 1);
            attachLongTaskToSpan(longTask, span);
            onLongTaskFound(longTask);
        }
    }
};
const onEnd$4 = (span, superOnEnd) => {
    if (span.instrumentationLibrary.name === INSTRUMENTATION.LONG_TASK) {
        span.attributes[LONG_TASK_ID] = uuid();
        if (!span.parentSpanId) {
            const bestParentSpan = findBestSpanInTime$1(span.startTime);
            if (bestParentSpan) {
                attachLongTaskToSpan(span, bestParentSpan);
            }
            else {
                // save this longtask for later and don't emit it yet
                longTasks.push(span);
                if (longTasks.length > MAX_LONG_TASKS_SIZE)
                    longTasks.shift();
                return;
            }
        }
    }
    else if (span.instrumentationLibrary.name !== INSTRUMENTATION.PERFORMANCE) {
        // save ended span, so it can be used for further longtasks
        spans$1.push(span);
        if (spans$1.length > MAX_SPANS_SIZE$1)
            spans$1.shift();
        // try previously ended longtasks without context
        attachBestLongTasks(span, superOnEnd);
    }
    superOnEnd(span);
};

const isXhrSpan = (span) => (span.name.indexOf(RESOURCE_XHR_NAME) === 0 || ['PUT', 'POST', 'DELETE', 'PATCH', 'GET'].includes(span.name)) && span.kind === SpanKind.CLIENT;
const isDocumentLoadSpan = (span) => span.name === AttributeNames.DOCUMENT_LOAD &&
    span.instrumentationLibrary.name === INSTRUMENTATION.DOCUMENT_LOAD;
const isNavigationSpan = (span) => span.name.indexOf(EVENT_NAVIGATION_NAME) === 0 &&
    span.instrumentationLibrary.name === INSTRUMENTATION.NAVIGATION;
const isLongtaskSpan = (span) => span.instrumentationLibrary.name === INSTRUMENTATION.LONG_TASK;
/**
 * http.action_type is a Cloud Observation specific attribute describing nature of a trace.
 * It's used to separate different top-level user interactions.
 */
const getPathName = (span, attribute = 'http.url') => {
    return new URL(span.attributes[attribute]).pathname || '';
};

const RESOURCE_PRECISION_NS = 1e7; // 10 milliseconds 点击事件
const RESOURCE_INIT_PRECISION_NS = 3e9; // 1s 路由跳转初始加载时间
const MAX_RESOURCES_SIZE = 10;
const MAX_SPANS_SIZE = 100;
const spans = [];
const resources = [];
const isTimeRangeInSpan = (resourceStartTimeNs, span) => {
    const spanStartTimeNs = hrTimeToNanoseconds(span.startTime);
    const spanEndTimeNs = hrTimeToNanoseconds(span.endTime);
    if (isDocumentLoadSpan(span) || isNavigationSpan(span)) {
        return spanEndTimeNs + RESOURCE_INIT_PRECISION_NS > resourceStartTimeNs;
    }
    else {
        return (resourceStartTimeNs + RESOURCE_PRECISION_NS >= spanStartTimeNs &&
            resourceStartTimeNs - RESOURCE_PRECISION_NS <= spanEndTimeNs);
    }
};
const attachResourceToSpan = (resource, parentSpan) => {
    // span.parentSpanId is readonly so we need to cast to 'any'
    resource.parentSpanId = parentSpan.spanContext().spanId;
    resource.spanContext().traceId = parentSpan.spanContext().traceId;
};
const findBestSpanInTime = (resourceStartTimeHrTime) => {
    const resourceStartTime = hrTimeToNanoseconds(resourceStartTimeHrTime);
    for (let i = spans.length - 1; i >= 0; i -= 1) {
        const span = spans[i];
        if (isTimeRangeInSpan(resourceStartTime, span))
            return span;
    }
};
const attachResourceTasks = (span, onResourceFound) => {
    for (let i = resources.length - 1; i >= 0; i -= 1) {
        const resource = resources[i];
        if (isTimeRangeInSpan(hrTimeToNanoseconds(resource.startTime), span)) {
            resources.splice(i, 1);
            attachResourceToSpan(resource, span);
            onResourceFound(resource);
        }
    }
};
const onEnd$3 = (span, superOnEnd) => {
    if (span.instrumentationLibrary.name === INSTRUMENTATION.RESOURCE) {
        if (!span.parentSpanId) {
            const bestParentSpan = findBestSpanInTime(span.startTime);
            if (bestParentSpan) {
                attachResourceToSpan(span, bestParentSpan);
            }
            else {
                resources.push(span);
                if (resources.length > MAX_RESOURCES_SIZE) {
                    // 找不到上下文的Resource span，但是不抛弃它
                    const shiftResource = resources.shift();
                    superOnEnd(shiftResource);
                }
                return;
            }
        }
    }
    else if (span.instrumentationLibrary.name === INSTRUMENTATION.USER_INTERACTION ||
        isDocumentLoadSpan(span)) {
        // save ended span, so it can be used for further resource
        spans.push(span);
        if (spans.length > MAX_SPANS_SIZE)
            spans.shift();
        // try previously ended longtasks without context
        attachResourceTasks(span, superOnEnd);
    }
    superOnEnd(span);
};

const dropSingleSpanTracesTraceProcessor = (rootSpan, spans) => {
    if (rootSpan.attributes[RUM_TYPE] === RumType.ACTION &&
        (spans.length === 1 && !rootSpan.attributes[ACTION_DOM_MUTATION]))
        return TraceProcessorResult.DROP_ROOT_SPAN;
};

// 在整个trace链路中增加action_id处理孩子span
const actionChildTraceProcessor = (rootSpan, readableSpan) => {
    if (INSTRUMENTATION.USER_INTERACTION === rootSpan.instrumentationLibrary.name &&
        !isNavigationSpan(rootSpan)) {
        const span = readableSpan;
        span.attributes[ACTION_ID] = rootSpan.attributes[ACTION_ID];
    }
};

var TraceProcessorResult;
(function (TraceProcessorResult) {
    TraceProcessorResult[TraceProcessorResult["DROP_ROOT_SPAN"] = 0] = "DROP_ROOT_SPAN";
})(TraceProcessorResult || (TraceProcessorResult = {}));
const TIMEOUT = 5000; // time to process metrics after last trace span start on end
const ROOT_PROCESSORS = [
    // 根节点处理
    dropSingleSpanTracesTraceProcessor,
];
const CHILD_PROCESSORS = [
    // 孩子节点处理
    actionChildTraceProcessor,
];
const traces = {};
const createTraceProcessor = (spanProcessor) => {
    const processTraceRecord = ({ traceId, rootSpan, send, spans, }) => {
        const isSend = !traces[traceId]; // 页面隐藏会马上发出去，防止重复发送
        if (!rootSpan || !send || isSend) {
            // !send标识还没结束
            // span 结束，但 root span 没有，所以删除整个trace
            return;
        }
        // 延迟删除traceId记录是因为来自forceFlush()的调用时候，未结束的rootsapn将会被删除，会导致processTraceChild失效
        delete traces[traceId];
        let shouldSend = true;
        ROOT_PROCESSORS.forEach((producer) => {
            const result = producer(rootSpan, spans, spanProcessor);
            if (result === TraceProcessorResult.DROP_ROOT_SPAN)
                shouldSend = false;
        });
        if (shouldSend)
            send();
    };
    const processTraceChild = ({ rootSpan }, span) => {
        if (!rootSpan)
            return;
        CHILD_PROCESSORS.forEach((producer) => {
            producer(rootSpan, span);
        });
    };
    // const forceFlush = () => {
    //   // we may have no other chance to process saved traces so we need to calculate and send them right now
    //   Object.values(traces).forEach((traceRecord) => {
    //     processTraceRecord(traceRecord)
    //   })
    // }
    function sendAllData() {
        // we may have no other chance to process saved traces so we need to calculate and send them right now
        Object.values(traces).forEach((traceRecord) => {
            processTraceRecord(traceRecord);
        });
    }
    let isAddListener = false;
    function addListener() {
        // 在span创建后才监听（为了让监听晚于Instrument中的监听
        if (!isAddListener) {
            window.addEventListener('beforeunload', () => {
                sendAllData();
                spanProcessor.forceFlush();
            });
            isAddListener = true;
        }
    }
    const processTraceRecordLater = (traceRecord) => {
        clearTimeout(traceRecord.timeout);
        traceRecord.timeout = setTimeout(() => {
            processTraceRecord(traceRecord);
        }, TIMEOUT);
    };
    return {
        forceFlush: () => {
            sendAllData();
        },
        onStart: (span) => {
            const traceId = span.spanContext().traceId;
            let traceRecord = traces[traceId];
            if (!traceRecord) {
                traceRecord = { traceId, timeout: -1, spans: [] };
                traces[traceId] = traceRecord;
            }
            if (!span.parentSpanId && !traceRecord.rootSpan)
                traceRecord.rootSpan = span;
            addListener();
            processTraceRecordLater(traceRecord);
        },
        onEnd: (readableSpan, superOnEnd) => {
            let shouldCallOnEnd = true;
            const span = readableSpan;
            const traceId = span.spanContext().traceId;
            // 多加校验，这个span是record且sample才加入trace计算的，防止有span是record但是不sample导致trace出错
            const traceFlags = span.spanContext().traceFlags;
            const traceRecord = traces[traceId];
            if (traceRecord && traceFlags) {
                traceRecord.spans.push(span);
                if (traceRecord.rootSpan === span &&
                    span.attributes[RUM_TYPE] === RumType.ACTION) {
                    // action根节点end时，卡住扭转流程等子span到达，无则抛弃
                    shouldCallOnEnd = false;
                    traceRecord.send = () => {
                        superOnEnd(readableSpan);
                    };
                }
                else {
                    // 子节点end时，需要从这个trace获取一些数据
                    processTraceChild(traceRecord, span);
                }
                processTraceRecordLater(traceRecord);
            }
            if (shouldCallOnEnd)
                superOnEnd(readableSpan);
        },
    };
};

const getResourceItem = (span) => {
    var _a, _b;
    const resources = (_b = (_a = otperformance).getEntriesByType) === null || _b === void 0 ? void 0 : _b.call(_a, 'resource');
    const item = resources.find((entry) => {
        return entry.name === span.attributes[SemanticAttributes.HTTP_URL];
    }) || {
        name: span.attributes[SemanticAttributes.HTTP_URL],
        initiatorType: '',
    };
    return item;
};
const getResourceType = (span) => {
    const timing = getResourceItem(span);
    const url = timing.name;
    const path = new URL(url).pathname || '';
    let type = ResourceType.OTHER;
    RESOURCE_TYPES_MAP.every((res) => {
        const _type = res[0];
        const isType = res[1];
        if (isType(timing.initiatorType, path)) {
            type = _type;
            return false;
        }
        return true;
    });
    return type;
};
const getHttpCode = (span) => {
    const item = getResourceItem(span);
    if (item)
        return item.responseStatus;
};
// onStart 这个时候获取不到span的其他信息，所以需要在end处理
const onEnd$2 = (readableSpan) => {
    const span = readableSpan;
    if (span.name === AttributeNames.RESOURCE_FETCH) {
        span.attributes[RESOURCE_TYPE] = getResourceType(span);
        if (span.instrumentationLibrary.name === INSTRUMENTATION.DOCUMENT_LOAD)
            // documentLoad未捕获HTTP_STATUS_CODE需要查找
            span.attributes[SemanticAttributes.HTTP_STATUS_CODE] = getHttpCode(span);
        span.attributes[SemanticAttributes.HTTP_METHOD] = 'GET';
    }
    else if (span.instrumentationLibrary.name === INSTRUMENTATION.XHR_TASK) {
        /*
          span.setAttribute(RESOURCE_TYPE, ResourceType.XHR )
          setAttribute会判断 _isSpanEnded?是的话无法设置成功，因此直接使用赋值
        */
        span.attributes[RESOURCE_TYPE] = ResourceType.XHR;
        // 低版本浏览器跨域请求会多发 CORS Preflight，这个无状态码
        const httpMethod = span.attributes[SemanticAttributes.HTTP_METHOD];
        if (httpMethod) {
            span.attributes[SemanticAttributes.HTTP_METHOD] = (httpMethod).toUpperCase();
        }
    }
    else if (span.instrumentationLibrary.name === INSTRUMENTATION.FETCH_TASK) {
        span.attributes[RESOURCE_TYPE] = ResourceType.FETCH;
        const httpMethod = span.attributes[SemanticAttributes.HTTP_METHOD];
        if (httpMethod) {
            span.attributes[SemanticAttributes.HTTP_METHOD] = (httpMethod).toUpperCase();
        }
    }
};

let obserRecord$1;
const recordDomMutation = () => {
    const span = obserRecord$1.recordViewSpan;
    // @ts-expect-error
    if (span && !span._ended)
        span.setAttribute(ACTION_DOM_MUTATION, true);
};
const clearPre$1 = () => {
    if (obserRecord$1.observer) {
        obserRecord$1.observer.disconnect();
        obserRecord$1.recordViewSpan = undefined;
    }
    clearTimeout(obserRecord$1.timeout);
};
const beginOberser$1 = (span) => {
    const isNewObser = obserRecord$1 && obserRecord$1.observer;
    if (!isNewObser) {
        obserRecord$1 = {
            observer: new MutationObserver(recordDomMutation),
            timeout: 0,
        };
    }
    clearPre$1(); // 清理上一个监听器
    obserRecord$1.recordViewSpan = span;
    obserRecord$1.observer.observe(document.documentElement, {
        attributes: true,
        characterData: true,
        childList: true,
        subtree: true,
    });
    obserRecord$1.timeout = setTimeout(() => {
        clearPre$1();
    }, 1000);
};
// Process增加action_id
// 开启观察器，判断这次点击是否引起dom的变化
const onStart$1 = (span) => {
    if (INSTRUMENTATION.USER_INTERACTION === span.instrumentationLibrary.name) {
        const actionId = uuid();
        span.setAttribute(ACTION_ID, actionId);
        beginOberser$1(span);
    }
};
const onEnd$1 = (readableSpan) => {
    if (INSTRUMENTATION.USER_INTERACTION ===
        readableSpan.instrumentationLibrary.name) {
        const span = readableSpan;
        if (!isNavigationSpan(span))
            span.attributes[ACTION_NAME] = getUserInteractionSpanNameId(span.name);
        else
            delete span.attributes[ACTION_ID];
    }
};

let obserRecord;
const recordLoadTime = () => {
    if (obserRecord.observer) {
        obserRecord.observer.disconnect();
        const span = obserRecord.recordViewSpan;
        if (span) {
            if (isDocumentLoadSpan(span)) {
                span.attributes[EACH_PERFORMANCE.VIEW_LOAD_TIME] = Math.max(obserRecord.recordMutationTimeMs, span.attributes[EACH_PERFORMANCE.VIEW_LOAD_TIME]);
            }
            else {
                span.attributes[EACH_PERFORMANCE.VIEW_LOAD_TIME] =
                    obserRecord.recordMutationTimeMs - obserRecord.navigationTimeMs;
            }
        }
    }
};
const recordLoadTimeLater = () => {
    clearTimeout(obserRecord.timeout);
    obserRecord.recordMutationTimeMs = performance.now();
    obserRecord.timeout = setTimeout(() => {
        recordLoadTime();
    }, 100);
};
const clearPre = () => {
    if (obserRecord.observer) {
        obserRecord.observer.disconnect();
        obserRecord.recordViewSpan = undefined;
    }
};
const beginOberser = (span) => {
    const isNewObser = obserRecord && obserRecord.observer;
    if (!isNewObser) {
        obserRecord = {
            observer: new MutationObserver(recordLoadTimeLater),
            timeout: 0,
        };
    }
    if (isDocumentLoadSpan(span) || isNavigationSpan(span)) {
        clearPre(); // 只有view来了才监听
        obserRecord.recordViewSpan = span;
        obserRecord.navigationTimeMs = performance.now();
        obserRecord.observer.observe(document.documentElement, {
            attributes: true,
            characterData: true,
            childList: true,
            subtree: true,
        });
    }
};

const performanceViewProcessor = (viewSpan, { isDocumentLoad }) => {
    if (isDocumentLoad) {
        // 只有DocumentLoadSpan需要采集很多数据
        addInitPerformance(viewSpan);
    }
};
function addInitPerformance(span) {
    onFCP(metric => analyticsCallback(metric, span)); // 指标测量页面从开始加载到页面内容的任何部分在屏幕上完成渲染的时间
    onTTFB(metric => analyticsCallback(metric, span));
    const navigation = getNavigationTiming(); // event里也有，但是op做了时间规整,旧版本数据,数据有误差
    if (navigation) {
        span.attributes[INIT_PERFORMANCE.FIRST_TIME] =
            navigation.responseEnd - navigation.fetchStart;
        span.attributes[INIT_PERFORMANCE.DOM_INTERACTIVE] =
            navigation.domInteractive;
        span.attributes[INIT_PERFORMANCE.DOM_CONTENT_LOADED] =
            navigation.domContentLoadedEventEnd;
        span.attributes[INIT_PERFORMANCE.DOM_COMPLETE] = navigation.domComplete;
        span.attributes[INIT_PERFORMANCE.LOAD_EVENT] = navigation.loadEventEnd;
        span.attributes[INIT_PERFORMANCE.RESOURCE_LOAD_TIME] =
            navigation.loadEventStart - navigation.domContentLoadedEventEnd;
        span.attributes[INIT_PERFORMANCE.DOM_PARSING_TIME] =
            navigation.domComplete - navigation.domInteractive;
        span.attributes[INIT_PERFORMANCE.DOM_READY_TIME] =
            navigation.domContentLoadedEventEnd;
        span.attributes[EACH_PERFORMANCE.VIEW_LOAD_TIME] = navigation.loadEventEnd; // 会在oberser中比较切换值
    }
}
function getNavigationTiming() {
    var _a, _b;
    const navigation = (_b = (_a = otperformance).getEntriesByType) === null || _b === void 0 ? void 0 : _b.call(_a, 'navigation')[0];
    if (navigation) {
        return navigation;
    }
    else {
        // 兼容旧版本浏览器
        const perf = otperformance;
        const performanceTiming = perf.timing;
        if (performanceTiming)
            return computeRelativePerformanceTiming(performanceTiming);
    }
}
function computeRelativePerformanceTiming(performanceTiming) {
    const navigationStart = performanceTiming.navigationStart;
    const result = {};
    Object.values(PerformanceTimingNames).forEach((key) => {
        if (hasKey(performanceTiming, key)) {
            const val = performanceTiming[key];
            if (val && isNumber(val))
                result[key] = val - navigationStart;
        }
    });
    return result;
}
function analyticsCallback(metric, span) {
    if (metric.name === 'FCP')
        span.attributes[INIT_PERFORMANCE.INIT_LOAD_FCP] = metric.value;
    else if (metric.name === 'TTFB')
        span.attributes[INIT_PERFORMANCE.TIME_TO_FIRST_BYTE] = metric.value;
    else if (metric.name === 'LCP')
        span.attributes[INIT_PERFORMANCE.INIT_LOAD_LCP] = metric.value;
}

const hasViewAndSameSession = (span, span2) => {
    // 判断两个view是否存在，以及前一个view和这个view是否在一个session
    if (span && span2) {
        return (span.attributes[SESSION_ID_ATTRIBUTE] ===
            span2.attributes[SESSION_ID_ATTRIBUTE]);
    }
    else {
        return false;
    }
};
const navigationViewProcessor = (span, { isNavigation, isDocumentLoad }, viewInfo) => {
    if (isNavigation) {
        const preViewSpan = viewInfo.preViewSpan;
        // 设置采样率后不一定会采集前一个view span
        if (hasViewAndSameSession(span, preViewSpan)) {
            span.attributes[PRE_VIEW_ID_ATTRIBUTE] = preViewSpan.attributes[VIEW_ID_ATTRIBUTE];
            span.attributes[PRE_VIEW_URL_ATTRIBUTE] = preViewSpan.attributes[VIEW_URL_ATTRIBUTE];
        }
        // 页面切换会走到这里
        const pathName = getPathName(span, VIEW_URL_ATTRIBUTE);
        setViewId(true, pathName, span);
    }
    if (isDocumentLoad)
        span.attributes[PRE_VIEW_URL_ATTRIBUTE] = document.referrer || '';
};

const PROCESSORS$1 = [
    navigationViewProcessor,
    performanceViewProcessor,
];
const viewInfo = {
    viewId: uuid(),
};
const getCurrentViewId = () => {
    return viewInfo === null || viewInfo === void 0 ? void 0 : viewInfo.viewId;
};
const getCurrentViewName = () => {
    return viewInfo === null || viewInfo === void 0 ? void 0 : viewInfo.viewName;
};
const getCurrentViewUrl = () => {
    return viewInfo === null || viewInfo === void 0 ? void 0 : viewInfo.viewUrl;
};
const getCurrentViewPathGroup = () => {
    if (viewInfo === null || viewInfo === void 0 ? void 0 : viewInfo.viewName)
        return viewInfo.viewName.replace(/\/[\w-]*\d+[\w-]*/g, '/?');
};
const setViewId = (canChange, // 满足切换条件
pathName, // 判断是否切换过
span) => {
    const changeViewId = canChange && viewInfo.viewName !== pathName;
    if (changeViewId) {
        viewInfo.viewId = uuid();
        viewInfo.viewName = pathName;
        viewInfo.viewUrl = span.attributes[VIEW_URL_ATTRIBUTE];
    }
    beginOberser(span);
    span.attributes[VIEW_ID_ATTRIBUTE] = getCurrentViewId();
};
const createViewProcessor = () => {
    return {
        onStart: (span) => {
            const pathName = getPathName(span, VIEW_URL_ATTRIBUTE);
            if (viewInfo.preViewSpan) {
                const preViewSpan = viewInfo.preViewSpan;
                const prePathName = getPathName(preViewSpan, VIEW_URL_ATTRIBUTE);
                setViewId(prePathName !== pathName, pathName, span);
            }
            else {
                setViewId(true, pathName, span);
            }
        },
        onEnd: (readableSpan) => {
            const span = readableSpan;
            const isNavigation = isNavigationSpan(span);
            const isDocumentLoad = isDocumentLoadSpan(span);
            if (isNavigation || isDocumentLoad) {
                PROCESSORS$1.forEach((producer) => {
                    producer(span, { isNavigation, isDocumentLoad }, viewInfo);
                });
                viewInfo.preViewSpan = span;
            }
        },
    };
};

var DropProcessorResult;
(function (DropProcessorResult) {
    DropProcessorResult[DropProcessorResult["DROP_SPAN"] = 0] = "DROP_SPAN";
    DropProcessorResult[DropProcessorResult["NO_DROP_SPAN"] = 1] = "NO_DROP_SPAN";
})(DropProcessorResult || (DropProcessorResult = {}));
function getResponseInfo(readableSpan) {
    // 获取请求头和
    const responseHeader = readableSpan.attributes[RESOURCE_RESPONSE_HEADERS];
    let headerMap;
    if (responseHeader) {
        headerMap = {};
        const arr = responseHeader.trim().split(/[\r\n]+/);
        arr.forEach((line) => {
            const parts = line.split(': ');
            const header = parts.shift();
            const value = parts.join(': ');
            headerMap[header] = value;
        });
    }
    let responseText = readableSpan.attributes[RESOURCE_RESPONSE_TEXT];
    if (responseText) {
        // 有的string无法转化
        try {
            responseText = JSON.parse(responseText);
        }
        catch (_a) { }
    }
    return { responseText, responseHeader: headerMap };
}
const dropOrSalvageSuccessResources = (readableSpan, spanProcessor) => {
    const { responseText, responseHeader } = getResponseInfo(readableSpan);
    const httpStatuCode = readableSpan.attributes[SemanticAttributes.HTTP_STATUS_CODE] || 0;
    delete readableSpan.attributes[RESOURCE_RESPONSE_HEADERS];
    delete readableSpan.attributes[RESOURCE_RESPONSE_TEXT];
    let drop = false;
    const traceFlags = readableSpan.spanContext().traceFlags;
    if (traceFlags === 1 && spanProcessor.dropSuccessResources) {
        // 看是否drop resource
        if (typeof spanProcessor.dropSuccessResources === 'boolean') {
            drop = httpStatuCode >= 200 && httpStatuCode < 400;
        }
        else {
            drop = spanProcessor.dropSuccessResources((readableSpan.attributes[SemanticAttributes.HTTP_STATUS_CODE] ||
                0), hrTimeToNanoseconds(readableSpan.endTime) -
                hrTimeToNanoseconds(readableSpan.startTime), responseText, responseHeader);
        }
    }
    else if (traceFlags === 0 && spanProcessor.salvageErrorResources) {
        // 提供给用户回捞resource函数
        let salvage;
        if (typeof spanProcessor.salvageErrorResources === 'boolean') {
            salvage = httpStatuCode < 200 || httpStatuCode >= 400; // 使用默认的
        }
        else {
            // 用户传递使用用户
            salvage = spanProcessor.salvageErrorResources((readableSpan.attributes[SemanticAttributes.HTTP_STATUS_CODE] ||
                0), hrTimeToNanoseconds(readableSpan.endTime) -
                hrTimeToNanoseconds(readableSpan.startTime), responseText, responseHeader);
        }
        if (salvage) {
            readableSpan.attributes[SALVAGE] = salvage;
            readableSpan.spanContext().traceFlags = 1;
        }
    }
    return drop;
};
const dropInitXHRFetch = (readableSpan) => {
    const ignoreResoureType = ['fetch', 'xhr'];
    return (readableSpan.name === AttributeNames.RESOURCE_FETCH &&
        ignoreResoureType.includes(readableSpan.attributes[RESOURCE_TYPE]));
};
const resourcesSampleResSet = (readableSpan) => {
    const traceFlags = readableSpan.spanContext().traceFlags;
    const resourceCollect = readableSpan.attributes[RESOURCE_IS_SEND];
    delete readableSpan.attributes[RESOURCE_IS_SEND];
    readableSpan.attributes[RESOURCE_TRACE_SAMPLED] = false;
    if (traceFlags === 0 && resourceCollect)
        readableSpan.spanContext().traceFlags = 1;
    else if (traceFlags === 1)
        readableSpan.attributes[RESOURCE_TRACE_SAMPLED] = true;
};
const dropResource = (readableSpan, spanProcessor) => {
    /*
      分成几种情况：
      1.ignoreUrls必须drop的span，去除掉documentLoad加载的rum发起的resource
      2.documentLoad采集上的xhr、fetch忽略掉
      3.resources经过采样标识为record时，分为两种，根据情况扭转状态
        3.1 要采样，但是为了不生成后端链路，所以设置了no_sample
        3.2 采样可以忽略了
      4.dropSuccessResources为true，状态2xx的span不上报，打捞错误resource
     */
    let drop = false;
    if (readableSpan.name === AttributeNames.RESOURCE_FETCH ||
        isXhrSpan(readableSpan)) {
        const url = (readableSpan.attributes[SemanticAttributes.HTTP_URL] ||
            '');
        drop = isUrlIgnored(url, spanProcessor.ignoreUrls); // 要满足之前的
        drop = drop || dropInitXHRFetch(readableSpan);
        resourcesSampleResSet(readableSpan);
        drop = drop || dropOrSalvageSuccessResources(readableSpan, spanProcessor);
    }
    return drop ? DropProcessorResult.DROP_SPAN : DropProcessorResult.NO_DROP_SPAN;
};
const errorRepeatRecord = {
    viewId: '',
    repeatMap: {},
}; // 同一个页面下重复错误超过三次不上报
const dropRepeatError = (readableSpan, spanProcessor) => {
    var _a, _b;
    const viewId = (readableSpan.attributes[VIEW_ID_ATTRIBUTE] || '');
    if (viewId !== errorRepeatRecord.viewId) {
        errorRepeatRecord.viewId = viewId;
        errorRepeatRecord.repeatMap = {};
    }
    const message = (((_b = (_a = readableSpan.events[0]) === null || _a === void 0 ? void 0 : _a.attributes) === null || _b === void 0 ? void 0 : _b['exception.message']) ||
        '');
    if (errorRepeatRecord.repeatMap[message])
        errorRepeatRecord.repeatMap[message]++;
    else
        errorRepeatRecord.repeatMap[message] = 1;
    return (!!spanProcessor.dropRepeatErrors &&
        errorRepeatRecord.repeatMap[message] > spanProcessor.dropRepeatErrors);
};
const dropError = (readableSpan, spanProcessor) => {
    var _a;
    let drop = false;
    if (readableSpan.instrumentationLibrary.name === INSTRUMENTATION.EXCEPTION &&
        readableSpan.events[0]) {
        if (spanProcessor.dropErrors) {
            const eventAttributes = (_a = readableSpan.events[0].attributes) !== null && _a !== void 0 ? _a : {};
            const spanAttributes = readableSpan.attributes;
            const attributes = Object.assign(Object.assign({}, eventAttributes), spanAttributes);
            const name = attributes['exception.type'];
            const message = attributes['exception.message'];
            const source = attributes['exception.source'];
            const stack = attributes['exception.stacktrace'];
            drop = spanProcessor.dropErrors(name, message, stack, source, attributes);
        }
        if (!drop && typeof spanProcessor.dropRepeatErrors === 'number')
            drop = dropRepeatError(readableSpan, spanProcessor);
    }
    return drop ? DropProcessorResult.DROP_SPAN : DropProcessorResult.NO_DROP_SPAN;
};
const PROCESSORS = [
    // 根节点处理
    dropError,
    dropResource,
];
const createDropProcessor = (spanProcessor) => {
    return {
        onEnd: (readableSpan, superOnEnd) => {
            let shouldSend = true;
            PROCESSORS.forEach((producer) => {
                const result = producer(readableSpan, spanProcessor);
                if (result === DropProcessorResult.DROP_SPAN)
                    shouldSend = false;
            });
            if (shouldSend)
                superOnEnd(readableSpan);
        },
    };
};

function addRumType(span) {
    const instrumentationName = span.instrumentationLibrary.name;
    if (isLongtaskSpan(span)) {
        // longTask
        span.attributes[RUM_TYPE] = RumType.LONGTASK;
    }
    else if (instrumentationName === INSTRUMENTATION.EXCEPTION) {
        // error
        span.attributes[RUM_TYPE] = RumType.ERROE;
    }
    else if (isDocumentLoadSpan(span) || isNavigationSpan(span)) {
        // view
        span.attributes[RUM_TYPE] = RumType.VIEW;
    }
    else if (instrumentationName === INSTRUMENTATION.USER_INTERACTION) {
        // action非view的
        span.attributes[RUM_TYPE] = RumType.ACTION;
    }
    else if (instrumentationName === INSTRUMENTATION.PERFORMANCE) {
        // action为performace的 记录cls和fid
        span.attributes[RUM_TYPE] = RumType.PERFORMANCE;
    }
    else if (span.name === AttributeNames.RESOURCE_FETCH ||
        instrumentationName === INSTRUMENTATION.XHR_TASK ||
        instrumentationName === INSTRUMENTATION.FETCH_TASK) {
        // 低版本浏览器预检请求会多一条resource不打标签处理
        if (span.name !== CORSP_REFLIGHT)
            span.attributes[RUM_TYPE] = RumType.RESOURCE;
    }
}
/* add attributes to all spans */
const onStart = (span) => {
    span.setAttribute(VIEW_URL_ATTRIBUTE, location.href);
};
const onEnd = (readableSpan) => {
    const span = readableSpan;
    addRumType(span);
    if (span.attributes[SemanticAttributes.HTTP_USER_AGENT])
        delete span.attributes[SemanticAttributes.HTTP_USER_AGENT];
};

class CloudObservationSpanProcessor extends BatchSpanProcessor {
    constructor(exporter, config) {
        var _a, _b;
        super(exporter, config);
        this.dropSuccessResources = (_a = config.dropSuccessResources) !== null && _a !== void 0 ? _a : false;
        this.salvageErrorResources = (_b = config.salvageErrorResources) !== null && _b !== void 0 ? _b : false;
        this.dropRepeatErrors =
            config.dropRepeatErrors === true
                ? SAME_ERROR_REPEAT_COUNT
                : config.dropRepeatErrors; // 处理用户传递true的情况
        this.ignoreUrls = config.ignoreUrls; // 用于忽略由documentLoad采集的数据
        this.serviceName = config.serviceName;
        this.dropErrors = config.dropErrors;
        this.traceProcessor = createTraceProcessor(this);
        this.viewProcessor = createViewProcessor();
        this.dropProcessor = createDropProcessor(this);
    }
    onStart(span, context = ROOT_CONTEXT) {
        onStart(span); // Location href
        onStart$1(span); // 给每个action打上actionId的tag
        //  有顺序依赖，viewProcessor（依赖basicAttribute的location.href）， trace（依赖Resource的改名）
        this.viewProcessor.onStart(span); // 给每个span打上viewId的tag
        this.traceProcessor.onStart(span); // 记录trace
        onStart$2(span, this.serviceName);
        super.onStart(span, context);
    }
    onEnd(span) {
        onEnd$2(span); // resource基础信息
        onEnd(span); // span类型以及删除一些无需上报的字段
        // ignoreProcessor依赖Resource状态
        this.dropProcessor.onEnd(span, (span1) => {
            // 使用回调而不是Promises，因为即使是立即解析的Promise也不会同步执行，这将在关闭页面之前结束跨度span时中断
            onEnd$3(span1, (span2) => {
                onEnd$4(span2, (span3) => {
                    onEnd$1(span3);
                    this.viewProcessor.onEnd(span3);
                    this.traceProcessor.onEnd(span3, (span4) => {
                        emitter.emit(RUMEvent.SPANPROCESSEND, span);
                        super.onEnd(span4);
                    });
                });
            });
        });
    }
    // https://github.com/open-telemetry/opentelemetry-js/issues/3489
    forceFlush() {
        this.traceProcessor.forceFlush();
        return super.forceFlush();
    }
}

function initRecordSpan(provider) {
    const tracer = provider.getTracer('@heytap/cloud-observation-rum');
    function recordSpan(name, callback, attributes) {
        const callbackIsAttr = typeof callback === 'object';
        const needFor = callbackIsAttr ? callback : attributes;
        const attributesFor = formatAttributes(needFor);
        attributesFor[RUM_TYPE] = 'custom';
        // 传递spanName、回调、attributes
        const span = tracer.startSpan(name, {
            attributes: attributesFor,
        });
        if (typeof callback === 'function') {
            context.with(trace.setSpan(context.active(), span), async () => {
                // 名称、 函数 、对象
                try {
                    await callback(span);
                }
                catch (e) {
                    span.recordException(e);
                }
                finally {
                    span.end();
                }
            });
        }
        else if (callbackIsAttr) {
            span.end();
        }
        return span;
    }
    return {
        recordSpan,
        tracer,
    };
}

const traceInitialize = ({ collectionSourceUrl, serviceName, traceSampleRate = DEFAULT_TRACE_SAMPLE_RATE, sessionSampleRate = 1, bufferMaxSpans = BUFFER_MAX_SPANS, maxExportBatchSize = MAX_EXPORT_BATCH_SIZE, bufferTimeout = BUFFER_TIMEOUT, dropSuccessResources, ignoreUrls = [], salvageErrorResources, resourceSampleRate = DEFAULT_RESOURCE_SAMPLE_RATE, dropRepeatErrors = SAME_ERROR_REPEAT_COUNT, dropErrors, exportUseXHR, }, resource) => {
    var _a, _b;
    const traceSampleRatebeNumber = (_a = tryNumber(traceSampleRate)) !== null && _a !== void 0 ? _a : 1;
    const sessionSampleRatebeNumber = (_b = tryNumber(sessionSampleRate)) !== null && _b !== void 0 ? _b : 1;
    const tracesResource = new Resource(Object.assign(Object.assign({}, resource.attributes), { 
        // This is a temporary solution not covered by the specification.
        // Was requested in https://github.com/open-telemetry/opentelemetry-specification/pull/570 .
        'trace.sampling.probability': traceSampleRatebeNumber, 'session.sampling.probability': sessionSampleRatebeNumber }));
    const sampler = new SessionIdRatioBasedSampler(sessionSampleRatebeNumber, traceSampleRatebeNumber, resourceSampleRate);
    const provider = new WebTracerProvider({
        resource: tracesResource,
        sampler,
    });
    provider.register({
        propagator: new W3CTraceContextPropagator(),
    });
    const collectorExporter = new OTLPTraceExporter({
        url: `${collectionSourceUrl}/v1/traces`,
        headers: exportUseXHR ? {} : undefined,
    });
    provider.addSpanProcessor(new GlobalAttributesSpanProcessor());
    provider.addSpanProcessor(new CloudObservationSpanProcessor(collectorExporter, {
        maxQueueSize: bufferMaxSpans,
        maxExportBatchSize,
        scheduledDelayMillis: bufferTimeout,
        serviceName,
        dropRepeatErrors,
        dropErrors,
        salvageErrorResources,
        ignoreUrls: [new RegExp(`^${collectionSourceUrl}/\S*`), ...ignoreUrls],
        dropSuccessResources,
    }));
    const { recordSpan } = initRecordSpan(provider);
    return {
        tracesResource,
        provider,
        sampler,
        getCurrentSessionId,
        recordSpan,
    };
};

class CloudObservationLogProcessor extends BatchLogRecordProcessor {
    constructor(exporter, config) {
        super(exporter, config);
    }
    onEmit(logRecord) {
        // 跟随trace，如果span没采样，那么log也不采样
        // if (logRecord.spanContext?.traceFlags !== SamplingDecision.RECORD_AND_SAMPLED) return
        logRecord.setAttribute(VIEW_ID_ATTRIBUTE, getCurrentViewId());
        logRecord.setAttribute(VIEW_NAME_ATTRIBUTE, getCurrentViewName());
        logRecord.setAttribute(VIEW_URL_ATTRIBUTE, getCurrentViewUrl());
        logRecord.setAttribute(VIEW_PATH_GROUP_ATTRIBUTE, getCurrentViewPathGroup());
        logRecord.setAttribute(SESSION_ID_ATTRIBUTE, getCurrentSessionId());
        super.onEmit(logRecord);
    }
}

const logsInitialize = ({ collectionSourceUrl, logLevel = DEFAULT_LOG_LEVEL, captureConsole, exportUseXHR, maxExportBatchSize, bufferMaxSpans, }, resource, sampler) => {
    const logResources = new Resource(Object.assign({}, resource.attributes));
    const loggerProvider = new LoggerProvider({
        resource: logResources,
    });
    const logExporter = new OTLPLogExporter({
        url: `${collectionSourceUrl}/v1/logs`,
        headers: exportUseXHR ? {} : undefined,
    });
    loggerProvider.addLogRecordProcessor(new GlobalAttributesLogRecordProcessor());
    loggerProvider.addLogRecordProcessor(new CloudObservationLogProcessor(logExporter, {
        maxExportBatchSize,
        maxQueueSize: bufferMaxSpans,
    }));
    const logger = loggerProvider.getLogger('@heytap/cloud-observation-rum');
    if (captureConsole && console) {
        trackConsoleList.forEach((con) => {
            if (logLevelCompare(con.logLevel)) {
                // eslint-disable-next-line no-console
                console[con.funField] = (...args) => {
                    const res = consoleValueToArgument(args);
                    recordLog(con.logLevel, JSON.stringify(res) || '');
                    con.originFun.apply(console, args);
                };
            }
        });
    }
    function logLevelCompare(printLog) {
        const printIndex = SeverityNumber[printLog];
        const logIndex = logLevel
            ? SeverityNumber[logLevel]
            : SeverityNumber.UNSPECIFIED;
        return printIndex >= logIndex;
    }
    function isLogEmit(printLog) {
        return sampler.sessionCanSend && logLevelCompare(printLog);
    }
    function recordLog(severityText, body, attribute) {
        const attributes = formatAttributes(attribute);
        if (isLogEmit(severityText)) {
            return logger.emit({
                severityNumber: SeverityNumber[severityText],
                severityText,
                body,
                attributes,
            });
        }
    }
    return {
        logResources,
        recordLog,
        logger,
        loggerProvider,
    };
};

function optionsInitialize(options) {
    const { collectionSourceUrl } = options;
    if (!collectionSourceUrl) {
        throw new Error('collectionSourceUrl needs to be defined to initialize Cloud Observation OpenTelemetry RUM');
    }
    const parsedCollectionSourceUrl = getCollectionSourceUrl(collectionSourceUrl);
    options.collectionSourceUrl = parsedCollectionSourceUrl;
    const exportUseXHR = isUseXHR(parsedCollectionSourceUrl);
    return Object.assign(Object.assign({}, options), { collectionSourceUrl: parsedCollectionSourceUrl, exportUseXHR });
}
// https://bugs.chromium.org/p/chromium/issues/detail?id=490015
function isUseXHR(collectionSourceUrl) {
    let exportUseXHR = false;
    try {
        const data = new Blob(['{}'], {
            type: 'application/json',
        });
        navigator.sendBeacon(`${collectionSourceUrl}/v1/traces`, data);
    }
    catch (e) {
        exportUseXHR = true;
    }
    return exportUseXHR;
}

var _a;
const useWindow = typeof window === 'object' && window != null;
const initialize = (initOptions) => {
    const options = optionsInitialize(initOptions);
    if (options.debug) {
        api.diag.setLogger(new api.DiagConsoleLogger(), {
            logLevel: api.DiagLogLevel.DEBUG,
        });
    }
    api.diag.info('initialize start');
    const { scopeAttributes, moduleName } = options;
    const { allResource } = resourceInitialize(options);
    const { sampler, provider: traceProvider, recordSpan, getCurrentSessionId, } = traceInitialize(options, allResource);
    const { recordLog } = logsInitialize(options, allResource, sampler);
    // const { meterProvider, recordMetric } = metricInitialize(options)
    const { registerInstrumentations, disableInstrumentations, } = instrumentationInitialize(options, traceProvider);
    const { cloudObservationExceptionInstrumentation, cloudObservationNavigationInstrumentation, } = registerInstrumentations();
    const setScopeAttribute = (key, value) => {
        return setGlobalAttribute(`ums.rum.custom.${key.trim()}`, maybeStringTrim(value));
    };
    const setModuleName = (value) => {
        setGlobalAttribute(MODULE_NAME, maybeStringTrim(value));
    };
    if (scopeAttributes) {
        Object.entries(scopeAttributes).forEach(([key, value]) => {
            setScopeAttribute(key, value);
        });
    }
    if (moduleName)
        setModuleName(moduleName);
    const result = {
        readyListeners: [],
        onReady: (callback) => {
            callback();
        },
        on: emitter.on,
        off: emitter.off,
        registerInstrumentations,
        disableInstrumentations,
        setScopeAttribute,
        setModuleName,
        getCurrentSessionId,
        recordMetric: () => {
            console.warn('2.0.0版本出于体积考虑，暂不提供recordMetric能力，待插件化能力完成后支持');
        },
        recordSpan,
        recordView: cloudObservationNavigationInstrumentation.recordCustomView.bind(cloudObservationNavigationInstrumentation),
        recordApiError: cloudObservationExceptionInstrumentation.recordApiError.bind(cloudObservationExceptionInstrumentation),
        recordError: cloudObservationExceptionInstrumentation.recordCustomError.bind(cloudObservationExceptionInstrumentation),
        recordLog,
        version: "2.1.1",
    };
    if (useWindow && typeof window.cloudObservationRum === 'object')
        Object.assign(window.cloudObservationRum, result);
    api.diag.info('initialize end');
    return result;
};
if (useWindow && typeof window.cloudObservationRum) {
    // 用于处理异步的，false ，标示打包类型为globals
    const readyListeners = (_a = window.cloudObservationRum) === null || _a === void 0 ? void 0 : _a.readyListeners;
    if (Array.isArray(readyListeners) && readyListeners.length) {
        window.cloudObservationRum.initialize = initialize;
        readyListeners.forEach(callback => callback());
    }
}

export { RUMEvent, initialize };
